import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import SharedHeader from "@/components/SharedHeader";
import PricingPlans from "@/components/PricingPlans";
import { apiClient, createBillingIdempotencyKey } from "@/api/client";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

const BILLING_TERMS_ERROR = "Please accept the Terms of Service before choosing a plan.";

export default function PaywallPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [billingAgreed, setBillingAgreed] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const checkoutKeys = useRef(new Map<string, string>());
    const billingAgreementRef = useRef<HTMLInputElement>(null);

    const handleSelectPlan = async (planKey: string) => {
        if (!billingAgreed) {
            setError(BILLING_TERMS_ERROR);
            billingAgreementRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            billingAgreementRef.current?.focus({ preventScroll: true });
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const key = checkoutKeys.current.get(planKey)
                ?? createBillingIdempotencyKey("checkout");
            checkoutKeys.current.set(planKey, key);
            const { checkout_url } = await apiClient.createCheckoutSession(planKey, key);
            window.location.href = checkout_url;
        } catch (err) {
            console.error("Failed to create checkout session", err);
            setError("Something went wrong starting the checkout. Please try again.");
            setIsLoading(false);
        }
    };

    // If user is already active, redirect to episodes
    if (user?.subscription_status === "active") {
        navigate("/episodes", { replace: true });
        return null;
    }

    return (
        <div className={cn("min-h-screen", theme.colors.backgroundCream)}>
            <SharedHeader />

            <main className="mx-auto max-w-7xl px-6 py-16 pt-24">
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-black mb-6 text-gray-900 dark:text-white">
                        You're Almost There
                    </h1>
                    <p className="text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
                        Pick a plan to start chatting with your podcasts. Cancel anytime with one click.
                    </p>
                </div>

                {error ? (
                    <div
                        id="paywall-notification"
                        className="fixed inset-x-4 top-24 z-50 mx-auto max-w-lg"
                        role="alert"
                        aria-live="assertive"
                        aria-atomic="true"
                    >
                        <div className={cn(
                            theme.borders.error,
                            theme.colors.errorBg,
                            theme.text.error,
                            theme.shadows.neubrutalism,
                            "rounded-xl p-4 text-center font-semibold",
                        )}>
                            {error}
                        </div>
                    </div>
                ) : null}

                <PricingPlans
                    onSelectPlan={handleSelectPlan}
                    isLoading={isLoading}
                    ctaText="Subscribe Now"
                />

                <div className={cn(
                    theme.colors.warmYellowBg,
                    theme.borders.neubrutalism,
                    theme.shadows.neubrutalismSm,
                    "mx-auto mt-8 max-w-2xl rounded-xl p-5 sm:p-6",
                )}>
                    <label className="flex cursor-pointer items-start gap-4">
                        <input
                            ref={billingAgreementRef}
                            type="checkbox"
                            checked={billingAgreed}
                            onChange={(event) => {
                                const agreed = event.target.checked;
                                setBillingAgreed(agreed);
                                if (agreed && error === BILLING_TERMS_ERROR) {
                                    setError(null);
                                }
                            }}
                            aria-invalid={error === BILLING_TERMS_ERROR}
                            aria-describedby={error === BILLING_TERMS_ERROR ? "paywall-notification" : undefined}
                            className="mt-0.5 h-6 w-6 shrink-0 rounded border-2 border-gray-400 text-primary-orange focus:ring-primary-orange"
                        />
                        <span className="text-base font-medium leading-7 text-gray-800 dark:text-gray-200 sm:text-lg">
                            I understand this is a recurring subscription that renews automatically
                            until I cancel, and I agree to the{" "}
                            <Link
                                to="/terms"
                                className={cn("font-semibold hover:underline", theme.text.primaryOrange)}
                            >
                                Terms of Service
                            </Link>
                            .
                        </span>
                    </label>
                </div>
            </main>
        </div>
    );
}
